import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import { alpha, styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import ThumbUpAltRoundedIcon from "@mui/icons-material/ThumbUpAltRounded";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownOffAltRoundedIcon from "@mui/icons-material/ThumbDownOffAltRounded";
import ThumbDownAltRoundedIcon from "@mui/icons-material/ThumbDownAltRounded";

import {
  Stack,
  useTheme,
  ButtonGroup,
  CardActions,
  CardContent,
  useMediaQuery,
  IconButton,
  Fade,
  Zoom,
} from "@mui/material";

import { fDate } from "../../utils/format-time";
import { HEADER, NAV } from "../../layouts/dashboard/config-layout";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import { useNavigate, useParams } from "react-router-dom";
import ArticleService from "src/services/Article.service";
import useAuth from "src/hooks/useAuth";
import DiscussionsIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import { useState } from "react";
import toast from "react-hot-toast";
import Scrollbar from "src/components/scrollbar";

// Styled Components
const StyledCard = styled(Card)(({ theme, isMobile }) => ({
  borderRadius: isMobile ? 0 : theme.spacing(3),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.9
  )} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
  backdropFilter: "blur(20px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
  overflow: "hidden",
}));

const ImageContainer = styled(Box)(({ theme, isMobile, verticleView }) => ({
  position: "absolute",
  height: isMobile || verticleView ? "28vh" : "100%",
  width: isMobile || verticleView ? "100%" : "60%",
  overflow: "hidden",
  cursor: "pointer",
  "& img": {
    transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  "&:hover img": {
    transform: "scale(1.05)",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: `linear-gradient(to ${
      isMobile || verticleView ? "top" : "left"
    }, 
			${theme.palette.background.paper} 0%, 
			${alpha(theme.palette.background.paper, 0.1)} 60%,
			transparent 100%)`,
    transition: "opacity 0.3s ease",
    opacity: 0.8,
  },
  "&:hover::after": {
    opacity: 0.4,
  },
}));

const StickyTitle = styled(Typography)(({ theme }) => ({
  position: "sticky",
  top: 0,
  paddingBottom: theme.spacing(2),
  zIndex: 1,
}));

const ActionButton = styled(Button)(({ theme, variant: buttonVariant }) => ({
  borderRadius: theme.spacing(2),
  textTransform: "none",
  fontWeight: 500,
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  marginLeft: -10,
  ...(buttonVariant === "action" && {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.grey[600],
      0.1
    )} 0%, ${alpha(theme.palette.grey[600], 0.05)} 100%)`,
    border: `1px solid ${alpha(theme.palette.grey[500], 0.3)}`,
    color: theme.palette.text.secondary,
    "&:hover": {
      background: `linear-gradient(135deg, ${alpha(
        theme.palette.grey[600],
        0.15
      )} 0%, ${alpha(theme.palette.grey[600], 0.08)} 100%)`,
      transform: "translateY(-1px)",
    },
  }),
}));

const LikeButton = styled(Button)(({ theme, isActive }) => ({
  background: isActive
    ? `linear-gradient(135deg, ${theme.palette.grey[600]} 0%, ${theme.palette.grey[700]} 100%)`
    : `linear-gradient(135deg, ${alpha(
        theme.palette.grey[600],
        0.1
      )} 0%, ${alpha(theme.palette.grey[600], 0.05)} 100%)`,
  border: `1px solid ${alpha(theme.palette.grey[500], 0.3)}`,
  color: isActive ? "white" : theme.palette.text.secondary,
  "&:hover": {
    background: isActive
      ? `linear-gradient(135deg, ${theme.palette.grey[700]} 0%, ${theme.palette.grey[600]} 100%)`
      : `linear-gradient(135deg, ${alpha(
          theme.palette.grey[600],
          0.15
        )} 0%, ${alpha(theme.palette.grey[600], 0.08)} 100%)`,
  },
}));

const DislikeButton = styled(Button)(({ theme, isActive }) => ({
  background: isActive
    ? `linear-gradient(135deg, ${theme.palette.grey[600]} 0%, ${theme.palette.grey[700]} 100%)`
    : `linear-gradient(135deg, ${alpha(
        theme.palette.grey[600],
        0.1
      )} 0%, ${alpha(theme.palette.grey[600], 0.05)} 100%)`,
  border: `1px solid ${alpha(theme.palette.grey[500], 0.3)}`,
  color: isActive ? "white" : theme.palette.text.secondary,
  "&:hover": {
    background: isActive
      ? `linear-gradient(135deg, ${theme.palette.grey[700]} 0%, ${theme.palette.grey[600]} 100%)`
      : `linear-gradient(135deg, ${alpha(
          theme.palette.grey[600],
          0.15
        )} 0%, ${alpha(theme.palette.grey[600], 0.08)} 100%)`,
  },
}));

const AuthorButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  textTransform: "none",
  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.background.paper,
      0.9
    )} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
    transform: "translateY(-1px)",
  },
}));

const ShareIconButton = styled(IconButton)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.8
  )} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.1
    )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
    transform: "translateY(-1px) scale(1.05)",
  },
}));

export default function FeedCard({
  article,
  onClick = () => {},
  setIsSharable = false,
  setOpen = true,
  setArticleid = "",
  setShareurl = "",
  verticleView = false,
  maxHeight,
}) {
  const {
    cover,
    coverImg,
    title,
    body,
    createdAt,
    like,
    likes,
    id,
    owner,
    isLiked,
    isDisliked,
  } = article;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const navigate = useNavigate();
  const { orgIdentifier } = useParams();
  const { user } = useAuth();
  const [hasLiked, setHasLiked] = useState(isLiked);
  const [hasDisliked, setHasDisliked] = useState(isDisliked);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpen = () => {
    if (!orgIdentifier) {
      setOpen(true);
      setArticleid(id);
    }
  };

  const toggleLike = () => {
    setHasLiked((prev) => !prev);
    if (hasDisliked) setHasDisliked(false);
  };

  const toggleDislike = () => {
    setHasDisliked((prev) => !prev);
    if (hasLiked) setHasLiked(false);
  };

  const handleLike = async (user) => {
    try {
      setIsLoading(true);
      toggleLike();
      await ArticleService.LikeArticle(user);
    } catch (error) {
      setHasLiked(isLiked); // Revert on error
      toast.error("Error liking article");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDislike = async (user) => {
    try {
      setIsLoading(true);
      toggleDislike();
      await ArticleService.DislikeArticle(user);
    } catch (error) {
      setHasDisliked(isDisliked); // Revert on error
      toast.error("Error disliking article");
    } finally {
      setIsLoading(false);
    }
  };

  const shareArticle = () => {
    let shareUrl = `https://open.riple.org/article/${id}`;
    shareUrl && setShareurl(shareUrl);
    setIsSharable(true);
  };

  const renderAvatar = (
    <Avatar
      alt={owner.name}
      src={owner.profilePic}
      sx={{
        width: 28,
        height: 28,
      }}
    />
  );

  const renderTitle = (
    <StickyTitle
      variant="h5"
      sx={{
        fontWeight: 700,
        lineHeight: 1.3,
        color: "text.primary",
        overflow: "hidden",
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 5,
        backgroundImage: `linear-gradient(to bottom, 
        ${theme.palette.background.paper} 80%, 
        transparent 100%)`,
      }}
    >
      {title}
    </StickyTitle>
  );

  const renderBody = (
    <Typography
      variant="body1"
      sx={{
        lineHeight: 1.7,
        color: "text.secondary",
        fontSize: { xs: 14, sm: 20 },
      }}
    >
      {body}
    </Typography>
  );

  const renderCover = (
    <Box
      component="img"
      alt={title}
      src={cover || coverImg}
      sx={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
      }}
    />
  );

  const renderDate = (
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        fontSize: 12,
      }}
    >
      {fDate(createdAt)}
    </Typography>
  );

  return (
    <Fade in timeout={400}>
      <StyledCard
        isMobile={isMobile}
        sx={{
          height:
            maxHeight ||
            (isMobile
              ? `calc(99dvh - ${NAV.MOBILE}px)`
              : `calc(100vh - ${HEADER.H_DESKTOP + 150}px )`),
          margin: "auto 0",
          pb: 2,
          mb: 2,
          scrollSnapAlign: isMobile ? "start" : "center",
        }}
      >
        <ImageContainer isMobile={isMobile} verticleView={verticleView}>
          {renderCover}
        </ImageContainer>

        <CardContent
          sx={{
            p: 3,
            cursor: "pointer",
            pl: isMobile || verticleView ? 3 : "62%",
            pr: isMobile || verticleView ? 3 : 4,
            pt: isMobile || verticleView ? "30vh" : 4,
            height: "calc(100% - 4.5rem)",
            pb: 5,
            overflow: "hidden",
            overflowY: "auto",
          }}
          onClick={onClick}
        >
          <Scrollbar>
            {renderTitle}
            {renderBody}
          </Scrollbar>
        </CardContent>
        <CardActions
          sx={{
            pb: 3,
            pr: isMobile || verticleView ? 3 : 2,
            pl: isMobile || verticleView ? 3 : 0,
            ml: isMobile || verticleView ? 0 : "62%",
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            height: "8rem",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <AuthorButton
                startIcon={renderAvatar}
                onClick={() => {
                  !orgIdentifier && navigate(`/profile/${owner._id}`);
                }}
                size="small"
              >
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color="text.primary"
                >
                  {owner.name}
                </Typography>
              </AuthorButton>
              <Typography sx={{ opacity: 0.5, mx: 1 }}>•</Typography>
              {renderDate}
            </Stack>

            <Zoom in timeout={600}>
              <ShareIconButton size="small" onClick={shareArticle}>
                <ShareRoundedIcon fontSize="small" />
              </ShareIconButton>
            </Zoom>
          </Stack>

          <Stack direction="row" justifyContent="space-between" ml="0">
            <ActionButton
              variant="action"
              onClick={handleOpen}
              startIcon={<DiscussionsIcon />}
              size="small"
            >
              Discussions
            </ActionButton>

            <ButtonGroup>
              <DislikeButton
                isActive={hasDisliked}
                disabled={isLoading}
                onClick={() => {
                  !orgIdentifier && handleDislike(user._id);
                }}
                size="small"
              >
                {hasDisliked ? (
                  <ThumbDownAltRoundedIcon fontSize="small" />
                ) : (
                  <ThumbDownOffAltRoundedIcon fontSize="small" />
                )}
              </DislikeButton>

              <LikeButton
                isActive={hasLiked}
                disabled={isLoading}
                startIcon={
                  hasLiked ? (
                    <ThumbUpAltRoundedIcon fontSize="small" />
                  ) : (
                    <ThumbUpAltOutlinedIcon fontSize="small" />
                  )
                }
                onClick={() => {
                  !orgIdentifier && handleLike(user._id);
                }}
                size="small"
              >
                {(like || likes) + (hasLiked ? 1 : 0)}
              </LikeButton>
            </ButtonGroup>
          </Stack>
        </CardActions>
      </StyledCard>
    </Fade>
  );
}

FeedCard.propTypes = {
  article: PropTypes.object.isRequired,
  onClick: PropTypes.func,
};
